import type { AccountTransactionDraft } from '@mls-wallet/shared-types';
import { buildTransactionSimulationRequest } from '../../components/transaction-builder/buildAccountTransactionDraft';
import type { TransactionBuilderParameterState } from '../../components/transaction-builder/parameter-inputs';
import type { RawContractTransaction } from '../../components/transaction-builder/raw-transaction-import';
import type {
  Contract,
  SelectedContractFunction,
} from '../../components/transaction-builder/types';
import type {
  ContractInteractionDetails,
  ContractTransactionImportSource,
} from '../../components/transaction-builder/useTransactionBuilderStore';

type LiveContractInteractionDetailsInput = {
  contract: Contract;
  selectedFunction: SelectedContractFunction | null;
  functionParameters: TransactionBuilderParameterState[];
  nativeTokenValue: string;
  nativeTokenDecimals: number;
  rawTransaction: RawContractTransaction | null;
  importSource: ContractTransactionImportSource | undefined;
};

type TransactionSimulationRequestInput = Parameters<
  typeof buildTransactionSimulationRequest
>[0];

/**
 * Builds the persisted and simulated contract call from the current form
 * fields, preventing those two representations from drifting as the user edits.
 */
export function buildLiveContractInteractionDetails({
  contract,
  selectedFunction,
  functionParameters,
  nativeTokenValue,
  nativeTokenDecimals,
  rawTransaction,
  importSource,
}: LiveContractInteractionDetailsInput): ContractInteractionDetails {
  return {
    // Select only Contract fields because restored store objects can carry
    // stale function, value, or raw-call details at runtime.
    address: contract.address,
    contractName: contract.contractName,
    implementationFunctions: contract.implementationFunctions,
    proxyFunctions: contract.proxyFunctions,
    allowedProxyFunctions: contract.allowedProxyFunctions,
    allowedImplementationFunctions: contract.allowedImplementationFunctions,
    functionParameters,
    ...(selectedFunction ? { selectedFunction } : {}),
    ...(nativeTokenValue !== ''
      ? {
          nativeTokenValue: {
            value: nativeTokenValue,
            decimals: nativeTokenDecimals,
          },
        }
      : {}),
    ...(rawTransaction ? { rawTransaction } : {}),
    ...(importSource ? { importSource } : {}),
  };
}

/** Return null when a type-complete live draft cannot yet be encoded. */
export function buildSimulationRequestOrNull(
  input: TransactionSimulationRequestInput
): AccountTransactionDraft | null {
  try {
    return buildTransactionSimulationRequest(input);
  } catch {
    // Case: Incomplete live edits may fail encoding and must not start a request.
    return null;
  }
}
